import { Suspense, useEffect, useContext, useMemo, useCallback, useState } from "react";
import { Paper } from "@mui/material";
import { useHistory, useLocation } from "react-router-dom";
import { CustomFormDialogType } from "../../../../../../types/components/dialogProps.types";
import PlanHeader from "../../../../../shared/enrollment/planCode/PlanHeader";
import { LazyBrandingActions, LazyCustomFormDialog, LazyPlanBranding } from "../../../../../shared";
import { BBIGLIV2, HealthBenefitBanner } from "../../../../../../assets";
import { BrandingProps, BrandingPropsV2 } from "../../../../../../types/components/enrollmentProps.types";
import { AuthContext, GroupOwnerContext } from "../../../../../../contexts";
import { CommonDetails, StandardDetails } from "../../../../../../types/enrollment.types";
import { generateGliWaiveEnrollmentPayload } from "../../../../../../utils/commonFunctions/groupEnrollment/enrollment";
import { api } from "../../../../../../utils/api";
import { AddEnrollmentPayload } from "../../../../../../utils/api/enrollment/enrollment";
import { BBI_PLAN_NAMES } from "../../../../../../constants/plan";
import CorePreviousBenefitDisclaimer from "../../../../../shared/CorePreviousBenefitDisclaimer/CorePreviousBenefitDisclaimer";
import { trackPromise } from "react-promise-tracker";
import { CheckPreviousCoreEnrollmentQueryTypes } from "../../../../../../utils/api/enrollment/getCheckPreviousCoreEnrollment";
import CorePreviousBenefitDisclaimerV2 from "../../../../../shared/CorePreviousBenefitDisclaimer/CorePreviousBenefitDisclaimerV2";

const GliBranding = ({ status, isActiveEnrollment, openEnrollment, changePlanProps }: BrandingPropsV2) => {
	const [hasPreviousCoreEnrollment, setHasPreviousCoreEnrollment] = useState<boolean>(false);
	const [waiveReason, setWaiveReason] = useState("");
	const { handleChangePlanButtonClick, buttonCaption } = changePlanProps;
	const { member } = useContext(AuthContext);
	const { groupOwner } = useContext(GroupOwnerContext);

	const location = useLocation();
	const history = useHistory();
	const urlSearchParams: URLSearchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
	const [isWaiveEnrollmentTriggered, setIsWaiveEnrollmentTriggered] = useState(false);
	const handleWaiveReasonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setWaiveReason(value);
	};

	const [customFormDialogProps, setCustomFormDialogProps] = useState<CustomFormDialogType>();

	const handleWaiveEnrollment = useCallback(async () => {
		if (member === null || openEnrollment === null) {
			return;
		}
		const { standardDetails, commonDetails } = generateGliWaiveEnrollmentPayload(
			member,
			openEnrollment,
			waiveReason
		);

		if (isActiveEnrollment) {
			// console.log("isActiveEnrollment", isActiveEnrollment);
			commonDetails.enrollment_status = "WAIVED - WAITING FOR APPROVAL";
			// debugger;
		}
		const enrollmentPayload: AddEnrollmentPayload = {
			standard_details: standardDetails,
			common_details: { ...commonDetails, agent_id: groupOwner && groupOwner._id ? groupOwner._id : null }
		};
		const response = await api.enrollment.addEnrollment(enrollmentPayload);
		// console.log("response", response);
		const step = urlSearchParams.get("step");
		if (response) {
			history.push(`?step=${step}&stage=2`);
		}
	}, [member, openEnrollment, waiveReason, isActiveEnrollment, groupOwner, urlSearchParams, history]);

	const handleWaiveButtonClick = useCallback(() => {
		if (status.openEnrollment === "CLOSED") {
			alert("Enrollment has not been opened for this plan");
			return;
		}
		setCustomFormDialogProps(
			Object.assign({}, customFormDialogProps, {
				openDialog: true,
				title: "Are you sure want to waive this enrollment?",
				textfields: [
					{
						label: "Waive Reason",
						onChange: handleWaiveReasonChange,
						value: waiveReason,
						placeholder: "Enter your reason for waive"
					}
				],
				actions: [
					{
						label: "Cancel",
						callback: () =>
							setCustomFormDialogProps(Object.assign({}, customFormDialogProps, { openDialog: false }))
					},
					{
						label: "Yes",
						callback: () => {
							setCustomFormDialogProps(
								Object.assign({}, customFormDialogProps, {
									openDialog: false
								})
							);
							// handleWaiveEnrollment();
							setIsWaiveEnrollmentTriggered(true);
						}
					}
				]
			})
		);
	}, [customFormDialogProps, status.openEnrollment, waiveReason]);

	const handleEnrollButtonClick = useCallback(() => {
		if (status.openEnrollment === "CLOSED") {
			alert("Enrollment has not been opened for this plan. Please contact your administrator");
		} else {
			const step = urlSearchParams.get("step");
			history.push(`?step=${step}&stage=1`);
		}
	}, [history, urlSearchParams, status]);

	const getCheckPreviousCoreEnrollment2023 = useCallback(async (group_number, SSN) => {
		const checkPreviousPayload: CheckPreviousCoreEnrollmentQueryTypes = {
			group_number,
			insured_SSN: SSN
		};

		const response = await trackPromise(api.enrollment.getCheckPreviousCoreEnrollment(checkPreviousPayload));

		if (response) {
			for (let idx = 0; idx < response.length; idx++) {
				const eachPlan = response[idx];

				if (eachPlan.plan_name === BBI_PLAN_NAMES.group_life_insurance) {
					setHasPreviousCoreEnrollment(true);
				}
			}
		}
	}, []);

	useEffect(() => {
		if (member) getCheckPreviousCoreEnrollment2023(member.group_number, member.SSN);
	}, [getCheckPreviousCoreEnrollment2023, member]);

	useEffect(() => {
		if (isWaiveEnrollmentTriggered) {
			handleWaiveEnrollment();
			setIsWaiveEnrollmentTriggered(false); // Reset the trigger
		}
	}, [isWaiveEnrollmentTriggered, handleWaiveEnrollment]);

	return (
		<div className="paper-form-container">
			<Suspense fallback={<div />}>
				{customFormDialogProps ? <LazyCustomFormDialog customFormDialogProps={customFormDialogProps} /> : null}
			</Suspense>
			<Paper className="theme-border-radius paper-container" elevation={1}>
				<PlanHeader planName="Group Life Insurance" planCode={openEnrollment?.plan?.plan_code} />
				<div className="theme-plan-section-margin" />
				<Suspense fallback={<div />}>
					{/* <LazyPlanBranding imgSrc={HealthBenefitBanner} /> */}
					<LazyPlanBranding imgSrc={BBIGLIV2} />
				</Suspense>
				{/* {hasPreviousCoreEnrollment ? <CorePreviousBenefitDisclaimer /> : null} */}
				{hasPreviousCoreEnrollment ? <CorePreviousBenefitDisclaimerV2 /> : null}
				<Suspense fallback={<div />}>
					<LazyBrandingActions
						waiveButtonCallback={handleWaiveButtonClick}
						enrollButtonCallback={handleEnrollButtonClick}
						handleChangePlanButtonClick={handleChangePlanButtonClick}
						buttonCaption={buttonCaption}
						planNameIndicator={BBI_PLAN_NAMES.group_life_insurance}
					/>
				</Suspense>
			</Paper>
		</div>
	);
};

export default GliBranding;
